import { type Option, SheetKeyRef, SheetNetworkKey, UserId } from "@tento-lona/sheets";
import { Uuid } from "@tento-core/uuid";
import { SheetId } from "@tento-lona/sheets";
import type { Network } from "../network.ts";

/**
 * `SheetKey` names a sheet on the server; a `SheetKeyRegistry` interns the
 * keys of one client (`client.sheetKeys`).
 *
 * Every spelling of the same sheet resolves to the same `SheetKey` instance
 * within one registry:
 * - `client.sheetKeys.make("ss_1234", null)`
 * - `client.sheetKeys.make("1234-1234-1234-1234", null)` — a raw UUID
 * - `client.sheetKeys.make(":lookup", null)` — the client's current user's sheet
 * - `client.sheetKeys.make(":lookup", "user-id")`
 * - `client.sheetKeys.make("[user-id]:lookup", null)`
 *
 * A key's `mapKey` is fixed when it is minted, so a `Map` keyed on it keeps
 * working as the registry learns more about the sheet (its id, lookup key,
 * owner).
 *
 * There is no process-wide registry and no process-wide current user: a bare
 * `:lookup` resolves against the user of the client that owns the registry
 * (`client.setCurrentUser`), so two clients signed in as different users
 * resolve the same spelling to their own sheets.
 */

/** What a key knows of its sheet; the registry that minted it enriches it in place. */
type SheetKeyData = {
  /** The sheet id's wire form (`ss_…`; `SheetId.toString()`). */
  id: Option<string>;
  /** The `:key` lookup key. */
  lookupKey: Option<string>;
  userId: Option<UserId>;
};

/**
 * Mints a `SheetKey` over the data object its registry keeps enriching.
 * Assigned by `SheetKey`'s static block (its constructor is private), so only
 * a `SheetKeyRegistry` mints keys.
 */
let mintKey: (data: SheetKeyData, mapKey: SheetKey.MapKey) => SheetKey;

/**
 * One client's sheet keys (`client.sheetKeys`): it interns every spelling of
 * a sheet to one `SheetKey` and resolves a bare `:lookup` against the
 * client's current user. Build one with the reader of the user it scopes to —
 * the client passes its own (`platformOf(client).ids.currentUser`); a test or
 * a tool without a client passes `() => UserId.of("user@example.com")` or `() => null`.
 */
export class SheetKeyRegistry {
  // Multiple hashes can point to the same canonical instance (aliases)
  readonly #hashToInstance = new Map<string, SheetKey>();
  /** Each minted key's data object, enriched in place. */
  readonly #dataOf = new WeakMap<SheetKey, SheetKeyData>();

  // Association mappings for computing hashes during lookup
  readonly #idToData = new Map<
    string,
    { lookupKey: Option<string>; userId: Option<UserId> }
  >();
  readonly #userLookupToId = new Map<string, string>(); // "[user]:lookup" -> id

  /** The user a bare `:lookup` resolves against, read at each resolution. */
  readonly #currentUser: () => Option<UserId>;

  constructor(currentUser: () => Option<UserId>) {
    this.#currentUser = currentUser;
  }

  /** The user a bare `:lookup` resolves against now, or `null` before one is bound. */
  get currentUser(): Option<UserId> {
    return this.#currentUser() ?? null;
  }

  /**
   * The canonical key for `key` in this registry — created on first use,
   * the same instance for every later spelling of the same sheet.
   *
   * @param key - Sheet identifier in various formats:
   *              - "ss_uuid" (prefixed sheet ID)
   *              - "uuid" (raw UUID)
   *              - ":lookup" (lookup key)
   *              - "[user]:lookup" (user-scoped lookup)
   * @param userId - The user a bare lookup key is scoped to, or `null` to
   *                 scope it to this registry's current user
   */
  make(key: string, userId: Option<UserId>): SheetKey {
    return this.#getOrCreate(key, userId);
  }

  /**
   * The canonical key for a raw network sheet. Reserved sheets (lookupKey +
   * creatorId) use the user-scoped lookup; all others the sheet UUID.
   */
  fromRaw(raw: Pick<Network.Sheet, "id" | "lookupKey" | "creatorId">): SheetKey {
    if (raw.lookupKey && raw.creatorId) {
      return this.make(raw.lookupKey, raw.creatorId);
    }
    return this.make(raw.id, null);
  }

  /**
   * Record what the server said about a sheet: its id, lookup key and owner,
   * and the provisional id it `replaces`. Idempotent; a key already minted
   * for any of these spellings is enriched in place and keeps its `mapKey`.
   */
  registerSheet(data: SheetKeyRegistry.Registration): void {
    const { id, lookupKey, userId, replaces } = data;

    // Validate lookup key format
    if (lookupKey && !lookupKey.startsWith(":")) {
      throw new Error(`Lookup key must start with ':'. Got: ${lookupKey}`);
    }

    // If this replaces a temporary ID, find and update that instance
    if (replaces) {
      const replacedHash = this.#computeHash({ id: replaces, lookupKey: null, userId: null });
      const existingInstance = this.#hashToInstance.get(replacedHash);

      if (existingInstance) {
        // Update the existing instance with new information
        this.#enrich(existingInstance, {
          id,
          lookupKey: lookupKey ?? existingInstance.lookupKey ?? null,
          userId: userId ?? existingInstance.userId ?? null,
        });

        // Add new hash mappings pointing to the same instance
        this.#addHashMapping(this.#computeHash({ id, lookupKey: null, userId: null }), existingInstance);

        if (lookupKey && userId) {
          this.#addHashMapping(
            this.#computeHash({ id: null, lookupKey, userId }),
            existingInstance,
          );
        }

        if (lookupKey) {
          this.#addHashMapping(
            this.#computeHash({ id: null, lookupKey, userId: null }),
            existingInstance,
          );
        }

        // Remove old ID mapping
        this.#idToData.delete(replaces);
      }
    }

    // Store or update associations
    const existingData = this.#idToData.get(id);
    this.#idToData.set(id, {
      lookupKey: lookupKey ?? existingData?.lookupKey ?? null,
      userId: userId ?? existingData?.userId ?? null,
    });

    if (lookupKey && userId) {
      const key = this.#makeUserLookupKey(userId, lookupKey);
      this.#userLookupToId.set(key, id);
    }

    // Find existing instance by any known hash and update it
    const possibleHashes = [
      this.#computeHash({ id, lookupKey: null, userId: null }),
      lookupKey && userId ? this.#computeHash({ id: null, lookupKey, userId }) : null,
      lookupKey ? this.#computeHash({ id: null, lookupKey, userId: null }) : null,
    ].filter((h): h is string => h != null);

    let existingInstance: Option<SheetKey> = null;
    for (const hash of possibleHashes) {
      existingInstance = this.#hashToInstance.get(hash) ?? null;
      if (existingInstance) break;
    }

    if (existingInstance) {
      // Update existing instance with new info
      this.#enrich(existingInstance, {
        id: id ?? existingInstance.id ?? null,
        lookupKey: lookupKey ?? existingInstance.lookupKey ?? null,
        userId: userId ?? existingInstance.userId ?? null,
      });

      // Add all hash mappings to this instance
      for (const hash of possibleHashes) {
        this.#addHashMapping(hash, existingInstance);
      }
    }
    // else: the instance is minted by the next `make`
  }

  #enrich(instance: SheetKey, update: SheetKeyData): void {
    const data = this.#dataOf.get(instance);
    if (data == null) return;
    data.id = update.id;
    data.lookupKey = update.lookupKey;
    data.userId = update.userId;
  }

  #getOrCreate(key: string, userIdHint: Option<UserId>): SheetKey {
    const parsed = this.#parse(key, userIdHint);
    const hash = this.#computeHash(parsed);

    // Check if we already have an instance for this hash
    let instance = this.#hashToInstance.get(hash);
    if (instance) {
      return instance;
    }

    // Check if we have an instance under different hashes (aliases)
    const aliasHashes = this.#getAliasHashes(parsed);
    for (const aliasHash of aliasHashes) {
      instance = this.#hashToInstance.get(aliasHash);
      if (instance) {
        // Enrich the existing instance with any new information we have
        this.#enrich(instance, {
          id: parsed.id ?? instance.id ?? null,
          lookupKey: parsed.lookupKey ?? instance.lookupKey ?? null,
          userId: parsed.userId ?? instance.userId ?? null,
        });

        // Add this hash as an alias to the same instance
        this.#addHashMapping(hash, instance);
        return instance;
      }
    }

    // Create new instance with immutable mapKey
    const mapKey = this.#computeMapKey(parsed);
    instance = mintKey(parsed, mapKey);
    this.#dataOf.set(instance, parsed);

    // Register all hashes for this instance
    this.#addHashMapping(hash, instance);
    for (const aliasHash of aliasHashes) {
      this.#addHashMapping(aliasHash, instance);
    }

    return instance;
  }

  /**
   * Parse a key string into normalized components.
   */
  #parse(key: string, userIdHint: Option<UserId>): SheetKeyData {
    // Format: [user]:lookup (colon is required)
    const userScopedMatch = key.match(/^\[([^\]]+)\]:(.+)$/);
    const scopedUser = userScopedMatch?.[1];
    const scopedLookup = userScopedMatch?.[2];
    if (scopedUser !== undefined && scopedLookup !== undefined) {
      const userId = UserId.of(scopedUser);
      const lookupKey = scopedLookup;
      const normalizedLookup = lookupKey.startsWith(":")
        ? lookupKey
        : `:${lookupKey}`;

      // Try to find the associated ID
      const userLookupKey = this.#makeUserLookupKey(userId, normalizedLookup);
      const id = this.#userLookupToId.get(userLookupKey) ?? null;

      return {
        id,
        lookupKey: normalizedLookup,
        userId,
      };
    }

    // Format: ss_uuid (prefixed sheet ID)
    if (key.startsWith("ss_")) {
      const sheetId = SheetId.of(key).toString();
      const data = this.#idToData.get(sheetId);

      return {
        id: sheetId,
        lookupKey: data?.lookupKey ?? null,
        userId: data?.userId ?? null,
      };
    }

    // Format: ephemeral(name) — legacy in-memory sheet id. `SheetId`
    // canonicalizes it to the deterministic `ss_` form, so resolve it
    // the same way as a prefixed id (and intern via the registry).
    if (key.startsWith("ephemeral(") && key.endsWith(")")) {
      const sheetId = SheetId.of(key).toString();
      const data = this.#idToData.get(sheetId);

      return {
        id: sheetId,
        lookupKey: data?.lookupKey ?? null,
        userId: data?.userId ?? null,
      };
    }

    // Format: raw UUID (convert to ss_ format)
    if (Uuid.is(key)) {
      const id = SheetId.of(key).toString();
      const data = this.#idToData.get(id);

      return {
        id,
        lookupKey: data?.lookupKey ?? null,
        userId: data?.userId ?? null,
      };
    }

    // Format: :lookup (lookup key, potentially with user hint or current user)
    if (key.startsWith(":")) {
      const lookupKey = key;
      // Use userIdHint if provided, otherwise this registry's current user
      const userId = userIdHint ?? this.#currentUser() ?? null;

      // Try to find the associated ID
      let id: Option<string> = null;
      if (userId) {
        const userLookupKey = this.#makeUserLookupKey(userId, lookupKey);
        id = this.#userLookupToId.get(userLookupKey) ?? null;
      }

      return {
        id,
        lookupKey,
        userId,
      };
    }

    throw new Error(`Invalid SheetKey format: ${key}`);
  }

  /**
   * Compute a hash for looking up existing instances.
   * This is used for internal registry lookups.
   */
  #computeHash(data: SheetKeyData): string {
    if (data.lookupKey && data.userId) {
      return `user-lookup:[${data.userId}]${data.lookupKey}`;
    }

    if (data.lookupKey) {
      return `lookup:${data.lookupKey}`;
    }

    if (data.id) {
      return `id:${data.id}`;
    }

    throw new Error("Cannot compute hash: insufficient data");
  }

  /**
   * The map key of a new `SheetKey` (`SheetKey.mapKey`): the same text as
   * `#computeHash`, fixed when the key is minted.
   *
   * Priority (most stable first):
   * 1. User + Lookup (e.g., "user-lookup:[lona-admin]:sys-health")
   * 2. Lookup only (e.g., "lookup::sys-health")
   * 3. ID (e.g., "id:ss_abc123")
   */
  #computeMapKey(data: SheetKeyData): SheetKey.MapKey {
    return this.#computeHash(data) as SheetKey.MapKey;
  }

  /**
   * Get all possible alias hashes for the given data.
   * Used to check if an instance already exists under a different representation.
   */
  #getAliasHashes(data: SheetKeyData): string[] {
    const hashes: string[] = [];

    if (data.id) {
      hashes.push(`id:${data.id}`);
    }

    if (data.lookupKey && data.userId) {
      hashes.push(`user-lookup:[${data.userId}]${data.lookupKey}`);
    }

    // Only add lookup-only hash if there's NO userId
    // This prevents [user]:lookup from colliding with :lookup
    if (data.lookupKey && !data.userId) {
      hashes.push(`lookup:${data.lookupKey}`);
    }

    return hashes;
  }

  #addHashMapping(hash: string, instance: SheetKey): void {
    this.#hashToInstance.set(hash, instance);
  }

  #makeUserLookupKey(userId: UserId, lookupKey: string): string {
    return `[${userId}]${lookupKey}`;
  }
}

/**
 * How a sheet is addressed on the server: by its id, or by a lookup key and
 * the user it belongs to.
 */
export class SheetKey {
  static {
    mintKey = (data, mapKey) => new SheetKey(data, mapKey);
  }

  /** Shared with the minting registry, which enriches it in place. */
  readonly #data: SheetKeyData;
  readonly #mapKey: SheetKey.MapKey;

  /** Frozen: the registry enriches the private data in place, never the instance. */
  private constructor(data: SheetKeyData, mapKey: SheetKey.MapKey) {
    this.#data = data;
    this.#mapKey = mapKey;
    Object.freeze(this);
  }

  /**
   * The sheet id's wire form (`ss_…`) if known; `sheetId` is the typed one.
   */
  get id(): Option<string> {
    return this.#data.id ?? null;
  }

  get sheetId(): Option<SheetId> {
    const id = this.#data.id;
    if (!id) return null;
    return SheetId.of(id);
  }

  /**
   * The `:key` lookup key if known.
   */
  get lookupKey(): Option<string> {
    return this.#data.lookupKey ?? null;
  }

  /**
   * Get the user ID if known.
   */
  get userId(): Option<UserId> {
    return this.#data.userId ?? null;
  }

  /** This key's plain form (`SheetKeyRef`): its id once known, else its lookup key and user. */
  toRef(): SheetKeyRef {
    const sheetId = this.sheetId;
    if (sheetId) return SheetKeyRef.fromId(sheetId);
    const lookupKey = this.lookupKey;
    if (!lookupKey) {
      throw new Error(
        `SheetKey has neither id nor lookup key: ${this.mapKey}`,
      );
    }
    return {
      kind: "lookup",
      lookupKey,
      userId: this.userId,
    };
  }

  /**
   * The key to file this sheet key under in a `Map` (`SheetKey.MapKey`):
   * fixed when the key is minted, so it never changes as the registry
   * enriches the key (its id, lookup key, owner). Not a sheet id.
   */
  get mapKey(): SheetKey.MapKey {
    return this.#mapKey;
  }

  /**
   * This key as a route spells it (`/api/sheets/{sheetKey}`): its canonical
   * text (`toString()`: `[user]:lookup`, `:lookup`, or the `ss_…` id when the
   * key has no lookup key), sent verbatim.
   */
  toNetworkKey(): SheetNetworkKey {
    return SheetNetworkKey.fromWire(this.toString());
  }

  /**
   * Get a canonical string representation.
   */
  toString(): string {
    // Canonicalize to the lookup key when present: a reserved /
    // lookup-bearing sheet's portable identity is its lookup key
    // (`:~lona:home`), not the per-user persisted UUID. Mirrors
    // `RowId.canonicalize`, `canonicalCells`, and `rowNetworkKeyForRead`,
    // which all prefer the symbolic key. `toRef()` stays the
    // wire-addressing form (id-first once persisted).
    if (this.#data.lookupKey && this.#data.userId) {
      return `[${this.#data.userId}]${this.#data.lookupKey}`;
    }

    if (this.#data.lookupKey) {
      return this.#data.lookupKey;
    }

    if (this.#data.id) {
      return this.#data.id;
    }

    return this.#mapKey;
  }
}

/** Types that go with `SheetKey`: `MapKey`. */
export namespace SheetKey {
  /**
   * A `SheetKey`'s map key (`SheetKey.mapKey`): the text a `Map` of sheet
   * keys is keyed on — `"user-lookup:[user]:lookup"`, `"lookup::lookup"` or
   * `"id:ss_…"`, fixed when the key is minted. Only a `SheetKeyRegistry`
   * makes one. Not a sheet id (`SheetId`) and not a route key
   * (`SheetNetworkKey`).
   */
  export type MapKey = string & { readonly __brand: "SheetKey.MapKey" };
}

/** Types that go with `SheetKeyRegistry`: `Registration`. */
export namespace SheetKeyRegistry {
  /**
   * What `registerSheet` records: a sheet's id and, where the server said,
   * its lookup key, owner and the provisional id it replaces (`null` where
   * unknown).
   */
  export type Registration = {
    id: SheetNetworkKey;
    lookupKey: Option<string>;
    userId: Option<UserId>;
    replaces: Option<SheetNetworkKey>;
  };
}
